import React, { useState, useMemo, useRef, useEffect, useId } from "react";
import { HeartPulse, Clock3, Zap, ChevronDown, Clock, ChevronRight, Sparkles } from "lucide-react";
import { Card } from "../ui/card";
import PoweringPredictionCard from "./PoweringPredictionCard";
import HealthInsightsHomeCard from "./HealthInsightsHomeCard";
import { useNavigate } from "react-router-dom";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceDot,
  ReferenceLine,
} from "recharts";
import { CHART_AXIS_TICK } from "../../utils/chartConfig";
import {
  gluceraHomeInterventionChipIdle,
  gluceraHomeInterventionChipSelected,
  gluceraHomeInterventionDropdownSelected,
  gluceraHomeInterventionStartButton,
} from "../../utils/gluceraTypography";
import { homeAccentChip, homeAccentIconShell, homeAccentLink, homeAccentText } from "../../utils/homeBlueTheme";
import { cn } from "../../lib/utils";
import { gluceraHomeChipBadge, gluceraHomeEyebrow } from "../../utils/gluceraTypography";
import { useQuery } from "@tanstack/react-query";
import homeService, { InterventionResponse, InterventionCurvePoint, type HomePageData } from "../../services/HomeService";
import { useEnsureSessionReady } from "../../hooks/useEnsureSessionReady";
import { validateMealId } from "../../utils/homePageUtils";
import { resolveMealDisplayName, formatMealTypeLabel } from "../../utils/resolveMealDisplayName";
import HomeBbsPillarsSummary from "./HomeBbsPillarsSummary";
import MealBbsScoreRow from "./MealBbsScoreRow";
import { getMealBbsLabel, getMealBbsScore } from "../../utils/mealBbsScore";

// ─── Types ────────────────────────────────────────────────────────────────────

interface GlucoseCurvePoint {
  time_min: number;
  glucose_mg_dl: number;
  phase?: string;
}

interface GlucoseSpikeData {
  spike_category: string;
  predicted_spike_mg_dl: number;
  this_meal_spike_mg_dl: number;
  time_to_peak_min: number;
  recovery_time_min: number;
  recommendations: string[];
  confidence: string;
  confidence_margin_mg_dl: number;
  previous_meal_remaining_mg_dl?: number;
  baseline_glucose?: number;
  glucose_curve?: { points: GlucoseCurvePoint[] };
}

interface MealData {
  meal_name?: string;
  product_name?: string;
  meal_type?: string;
  title?: string;
  meal_title?: string;
  meal_items?: Array<{
    name: string;
    calorie_content_kcal?: number;
    carbohydrate_content_g?: number;
    GI_estimate?: number;
  }>;
  meal_totals?: {
    calorie_content_kcal?: number;
    carbohydrate_content_g?: number;
    GI_estimate?: number;
  };
}

interface GlucoseInterventionCardProps {
  glucoseSpikeData?: GlucoseSpikeData;
  mealId?: string;
  latestMealData?: MealData;
  homeData?: HomePageData;
  selectedDate?: string;
  showScorePillars?: boolean;
  onStartIntervention?: (activity: { activityKey: string; label: string }) => void;
}

// ─── Activity list ─────────────────────────────────────────────────────────────

interface Activity {
  id: string;
  label: string;
  activityKey: string;
}

const QUICK_EXERCISES: Activity[] = [
  { id: "walking", label: "Walk", activityKey: "walking" },
  { id: "jumping_jacks", label: "Jacks", activityKey: "jumping_jacks" },
];

const ALL_ACTIVITIES: Activity[] = [
  { id: "walking",         label: "Walking",            activityKey: "walking" },
  { id: "brisk_walk",      label: "Brisk walk",         activityKey: "brisk_walk" },
  { id: "fast_walk",       label: "Fast walk",          activityKey: "fast_walk" },
  { id: "uphill_walk",     label: "Uphill walk",        activityKey: "uphill_walk" },
  { id: "spot_jogging",    label: "Jogging in place",   activityKey: "spot_jogging" },
  { id: "slow_jog",        label: "Slow jog",           activityKey: "slow_jog" },
  { id: "running",         label: "Running",            activityKey: "running" },
  { id: "cycling",         label: "Cycling",            activityKey: "cycling_moderate" },
  { id: "stationary_bike", label: "Stationary bike",    activityKey: "stationary_bike" },
  { id: "jumping_jacks",   label: "Jumping jacks",      activityKey: "jumping_jacks" },
  { id: "stair_climbing",  label: "Stair climbing",     activityKey: "stair_climbing" },
  { id: "weights",         label: "Weights",            activityKey: "weights" },
  { id: "circuit",         label: "Circuit training",   activityKey: "circuit_training" },
  { id: "hiit",            label: "HIIT",               activityKey: "hiit_moderate" },
  { id: "yoga",            label: "Yoga",               activityKey: "yoga" },
  { id: "swimming",        label: "Swimming",           activityKey: "swimming" },
  { id: "dancing",         label: "Dancing",            activityKey: "dancing" },
  { id: "tennis",          label: "Tennis",             activityKey: "tennis" },
  { id: "basketball",      label: "Basketball",         activityKey: "basketball" },
  { id: "football",        label: "Football",           activityKey: "football" },
];

function aggregateMealItemsNutrition(
  items: Array<{
    calorie_content_kcal?: number;
    carbohydrate_content_g?: number;
    GI_estimate?: number;
  }>
): { kcal: number; carbs: number; giAvg: number } {
  if (!items.length) return { kcal: 0, carbs: 0, giAvg: 0 };
  let kcal = 0;
  let carbs = 0;
  let giSum = 0;
  let giCount = 0;
  for (const it of items) {
    kcal += Number(it.calorie_content_kcal) || 0;
    carbs += Number(it.carbohydrate_content_g) || 0;
    const g = it.GI_estimate;
    if (g != null && Number.isFinite(Number(g))) {
      giSum += Number(g);
      giCount += 1;
    }
  }
  return { kcal, carbs, giAvg: giCount ? giSum / giCount : 0 };
}

function firstFiniteNumber(
  ...vals: Array<number | undefined | null>
): number | undefined {
  for (const v of vals) {
    if (v == null) continue;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

// ─── Tooltip ──────────────────────────────────────────────────────────────────

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-border bg-card/95 px-3 py-2 shadow-lg backdrop-blur-sm font-sans">
      <p className="mb-1 text-compact-xs font-semibold text-muted-foreground">{label}m</p>
      {payload.map((entry: any) => (
        <p key={entry.dataKey} className="text-compact-xs font-bold" style={{ color: entry.stroke }}>
          {entry.name}: {entry.value} mg/dL
        </p>
      ))}
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────

const GlucoseInterventionCard: React.FC<GlucoseInterventionCardProps> = ({
  glucoseSpikeData,
  mealId,
  latestMealData,
  homeData,
  selectedDate,
  showScorePillars,
  onStartIntervention,
}) => {
  const navigate = useNavigate();
  const { canUseAuthenticatedApis } = useEnsureSessionReady();
  const validMealId = validateMealId(mealId ?? null);
  const [selectedExercise, setSelectedExercise] = useState<Activity>(QUICK_EXERCISES[0]);
  const [showMoreDropdown, setShowMoreDropdown] = useState(false);
  // Detail (numbers + chart + comparison) collapsed by default — only a summary bar shows.
  const [detailOpen, setDetailOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isActionSelected = true;

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setShowMoreDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── API call ──────────────────────────────────────────────────────────────
  const { data: apiData, isLoading } = useQuery<InterventionResponse | null>({
    queryKey: ["intervention-calculate", validMealId, selectedExercise.activityKey],
    queryFn: () =>
      homeService.calculateIntervention(validMealId!, selectedExercise.activityKey),
    enabled: canUseAuthenticatedApis && Boolean(validMealId) && isActionSelected,
    staleTime: 5 * 60 * 1000,
  });

  // ── Values from APIs only (intervention/calculate + food-home-page glucose_spike) ──
  const curvePoints = glucoseSpikeData?.glucose_curve?.points ?? [];
  const curveNow = curvePoints.length > 0
    ? curvePoints.reduce((min, p) => p.time_min < min.time_min ? p : min)
    : undefined;
  const curvePeak = curvePoints.length > 0
    ? curvePoints.reduce((max, p) => p.glucose_mg_dl > max.glucose_mg_dl ? p : max)
    : undefined;

  const spikeFromHome = firstFiniteNumber(
    glucoseSpikeData?.this_meal_spike_mg_dl,
    glucoseSpikeData?.predicted_spike_mg_dl,
  );
  const baselineFromHome = firstFiniteNumber(glucoseSpikeData?.baseline_glucose);
  const peakFromHome =
    baselineFromHome != null && spikeFromHome != null
      ? baselineFromHome + spikeFromHome
      : undefined;

  const nowGl = firstFiniteNumber(
    apiData?.now_glucose,
    curveNow?.glucose_mg_dl,
    baselineFromHome,
  );

  const noActionPeakGl = firstFiniteNumber(
    apiData?.original_peak,
    curvePeak?.glucose_mg_dl,
    peakFromHome,
  );

  const peakMin = firstFiniteNumber(
    apiData?.time_to_peak_min,
    glucoseSpikeData?.time_to_peak_min,
  );

  const interventionCurvePoints = apiData?.glucose_forecast?.intervention_curve?.points ?? [];
  const interventionPeakPoint =
    peakMin != null
      ? interventionCurvePoints.find((p) => p.time_min === peakMin)
      : interventionCurvePoints.length > 0
        ? interventionCurvePoints.reduce((max, p) =>
            p.glucose_mg_dl > max.glucose_mg_dl ? p : max
          )
        : undefined;

  const newPeakGl = firstFiniteNumber(
    interventionPeakPoint?.glucose_mg_dl,
    apiData?.new_peak,
  );

  const dropMgDl =
    firstFiniteNumber(apiData?.intervention_drop_mg_dl) ??
    (noActionPeakGl != null && newPeakGl != null
      ? noActionPeakGl - newPeakGl
      : undefined);

  const bestWindow = firstFiniteNumber(apiData?.best_window_minutes_remaining);
  const minNeeded = firstFiniteNumber(apiData?.minutes_needed);

  const interventionAvailable = apiData?.intervention_available !== false;
  const showWalkSection =
    interventionAvailable &&
    (minNeeded != null ||
      dropMgDl != null ||
      newPeakGl != null ||
      (interventionCurvePoints.length > 0 &&
        apiData?.glucose_forecast?.intervention_curve != null));
  const showGlucoseSummary = nowGl != null || noActionPeakGl != null;

  // ── Chart data (API curves only — no synthetic intervention line) ───────────
  const chartData = useMemo(() => {
    const toChartPoint = (p: InterventionCurvePoint | GlucoseCurvePoint) => ({
      t: p.time_min,
      value: Math.round(p.glucose_mg_dl),
    });

    const noActionPts: { t: number; value: number }[] =
      apiData?.glucose_forecast?.no_action_curve?.points?.map(toChartPoint) ??
      curvePoints.map(toChartPoint);

    const actionPts: { t: number; value: number }[] =
      apiData?.glucose_forecast?.intervention_curve?.points?.map(toChartPoint) ?? [];

    const allT = new Set([...noActionPts.map((p) => p.t), ...actionPts.map((p) => p.t)]);
    const noMap = Object.fromEntries(noActionPts.map((p) => [p.t, p.value]));
    const acMap = Object.fromEntries(actionPts.map((p) => [p.t, p.value]));

    return [...allT].sort((a, b) => a - b).map((t) => ({
      t,
      noAction: noMap[t] ?? null,
      withAction: acMap[t] ?? null,
    }));
  }, [apiData, curvePoints]);

  const noActionPeakT = chartData.reduce(
    (best, p) => (p.noAction ?? 0) > (best.noAction ?? 0) ? p : best,
    chartData[0] ?? { t: 0, noAction: 0, withAction: 0 }
  );
  const actionPeakT = chartData.reduce(
    (best, p) => (p.withAction ?? 0) > (best.withAction ?? 0) ? p : best,
    chartData[0] ?? { t: 0, noAction: 0, withAction: 0 }
  );

  /** Don’t reserve chart height when there’s nothing to draw (avoids empty white band). */
  const hasRenderableChart = useMemo(() => {
    const valid = chartData.filter(
      (p) =>
        (p.noAction != null && Number.isFinite(Number(p.noAction))) ||
        (p.withAction != null && Number.isFinite(Number(p.withAction)))
    );
    return valid.length >= 2;
  }, [chartData]);

  const chartGradId = useId().replace(/:/g, "");
  const glActionGrad = `gl-action-${chartGradId}`;
  const glNoActionGrad = `gl-noaction-${chartGradId}`;
  /** Readable on light + dark chart bg */
  const strokeNoAction = "#e11d48";
  const strokeAction = "#6b7280";
  const fillNoActionMuted = "#e11d48";
  /* Softer emerald for "improvement" cue (still calm) */
  const strokeImprove = "#059669";

  // Format time
  const formatTime = (mins: number) => {
    const hours = Math.floor(mins / 60);
    const minutes = mins % 60;
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes} min`;
  };

  // Extract meal data
  const mealTypeLabel =
    formatMealTypeLabel(latestMealData?.meal_type) || "Meal";
  const mealItems = latestMealData?.meal_items || [];
  const mealTotals = latestMealData?.meal_totals;
  const apiItems = apiData?.meal_items ?? [];
  const itemsForNutrition =
    mealItems.length > 0 ? mealItems : apiItems.length > 0 ? apiItems : [];
  const aggItems = aggregateMealItemsNutrition(itemsForNutrition);

  const totalCalories =
    firstFiniteNumber(
      apiData?.calorie_content_kcal,
      apiData?.meal_totals?.calorie_content_kcal,
      mealTotals?.calorie_content_kcal,
      apiData?.total_calories,
      aggItems.kcal > 0 ? aggItems.kcal : undefined
    );

  const totalCarbs =
    firstFiniteNumber(
      apiData?.carbohydrate_content_g,
      apiData?.meal_totals?.carbohydrate_content_g,
      mealTotals?.carbohydrate_content_g,
      apiData?.total_carbohydrates_g,
      aggItems.carbs > 0 ? aggItems.carbs : undefined
    );

  const avgGI =
    firstFiniteNumber(
      apiData?.GI_estimate,
      apiData?.meal_totals?.GI_estimate,
      mealTotals?.GI_estimate,
      apiData?.gi_estimate,
      aggItems.giAvg > 0 ? aggItems.giAvg : undefined
    );

  const mealCardTitle = resolveMealDisplayName({
    mealName: apiData?.meal_name ?? latestMealData?.meal_name,
    mealTitle:
      (latestMealData as { title?: string; meal_title?: string } | undefined)
        ?.title ??
      (latestMealData as { meal_title?: string } | undefined)?.meal_title,
    productName: latestMealData?.product_name,
    mealType: latestMealData?.meal_type,
    mealItems: itemsForNutrition,
    fallback: "Your meal",
  });

  const dropRounded = dropMgDl != null ? Math.round(dropMgDl) : null;
  const logButtonLabel =
    minNeeded != null
      ? dropRounded != null && dropRounded > 0
        ? `Log ${minNeeded}-min ${selectedExercise.label.toLowerCase()} (−${dropRounded} mg/dL)`
        : `Log ${minNeeded}-min ${selectedExercise.label.toLowerCase()}`
      : null;
  const peakDropSubtitle =
    dropRounded != null && dropRounded > 0
      ? `Peak drops −${dropRounded} mg/dL · best window now`
      : bestWindow != null
        ? "Best window now"
        : null;

  return (
    <div className="space-y-4">{/* Main Meal Status Card */}
    <Card className="overflow-hidden rounded-2xl border border-border/60 bg-gradient-to-br from-background via-background to-muted/5 shadow-lg">
      <div className="p-4 space-y-4">
        {/* Header - Meal is live */}
        <div className="space-y-1">
          <h2 className="text-2xl font-bold text-foreground">
            {mealTypeLabel} is live.
          </h2>
          {bestWindow != null ? (
            <p className="text-sm text-muted-foreground">
              Your curve is still changeable for the next{" "}
              <span className="font-semibold text-foreground">{formatTime(bestWindow)}</span>.
            </p>
          ) : null}
        </div>

        {/* Status Badges */}
        {peakMin != null ? (
        <div className="flex gap-2 flex-wrap">
          {bestWindow != null && peakMin != null ? (
          <div className={cn("flex items-center gap-1.5 rounded-full px-3 py-1.5", homeAccentChip)}>
            <Clock className={cn("h-3.5 w-3.5", homeAccentText)} />
            <span className={cn("text-xs font-semibold uppercase tracking-wide", homeAccentText)}>
              {Math.round(Math.max(0, peakMin - bestWindow))} min after meal
            </span>
          </div>
          ) : null}
          <div className={cn("flex items-center gap-1.5 rounded-full px-3 py-1.5", homeAccentChip)}>
            <span className={cn("text-xs font-semibold uppercase tracking-wide", homeAccentText)}>
              Peak in {peakMin} min
            </span>
          </div>
        </div>
        ) : null}

        {/* Meal Card */}
        <div className="rounded-xl bg-muted/30 p-3 border border-border/50">
          <div className="flex items-start gap-3">
            {/* Meal Icon */}
            <div className={cn("flex h-12 w-12 shrink-0 items-center justify-center rounded-lg", homeAccentIconShell)}>
              <svg viewBox="0 0 24 24" className={cn("h-6 w-6", homeAccentText)} fill="currentColor">
                <path d="M8.1 13.34l2.83-2.83L3.91 3.5c-1.56 1.56-1.56 4.09 0 5.66l4.19 4.18zm6.78-1.81c1.53.71 3.68.21 5.27-1.38 1.91-1.91 2.28-4.65.81-6.12-1.46-1.46-4.2-1.1-6.12.81-1.59 1.59-2.09 3.74-1.38 5.27L3.7 19.87l1.41 1.41L12 14.41l6.88 6.88 1.41-1.41L13.41 13l1.47-1.47z"/>
              </svg>
            </div>

            {/* Meal Details */}
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-foreground line-clamp-2">
                {mealCardTitle}
              </h3>
              <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                {totalCalories != null && totalCalories > 0 && (
                  <span className="font-medium">
                    <span className="font-semibold text-foreground">{Math.round(totalCalories)}</span> kcal
                  </span>
                )}
                {totalCarbs != null && totalCarbs > 0 && (
                  <span className="font-medium">
                    <span className="font-semibold text-foreground">{Math.round(totalCarbs)}</span> g carbs
                  </span>
                )}
                {avgGI != null && avgGI > 0 && (
                  <span className="font-medium">
                    GI <span className="font-semibold text-foreground">{Math.round(avgGI)}</span>
                  </span>
                )}
              </div>
            </div>
          </div>
          {(() => {
            const interventionScore = apiData?.bbs_score;
            const interventionLabel = apiData?.bbs_label;
            const mealRecord = latestMealData as Record<string, unknown> | undefined;
            const mealDataScore = getMealBbsScore(mealRecord);
            const mealDataLabel = getMealBbsLabel(mealRecord);

            const score =
              interventionScore != null
                ? Math.round(Number(interventionScore))
                : mealDataScore != null
                  ? Math.round(mealDataScore)
                  : null;
            const label =
              interventionScore != null
                ? (interventionLabel ?? "")
                : (mealDataLabel ?? "");

            if (score == null) return null;

            return (
              <MealBbsScoreRow
                className="mt-3"
                score={score}
                label={label}
              />
            );
          })()}
        </div>
      </div>
    </Card>

    {(showWalkSection || showGlucoseSummary) ? (
    <Card className="overflow-hidden rounded-2xl border border-border/60 bg-gradient-to-br from-background via-background to-muted/5 shadow-lg">
      <div className="p-4 space-y-4">
        {/* ACTION ALERT — only when intervention API provides walk data */}
        {showWalkSection && minNeeded != null ? (
        <div className="-mx-4 border-y border-border/40 bg-white dark:bg-card">
          <div className="px-4 py-3">
            <h3 className="text-base font-bold text-foreground">
              Start {minNeeded}-min {selectedExercise.label.toLowerCase()}
            </h3>
            {peakDropSubtitle ? (
              <p className={cn("mt-1 text-compact-sm font-medium", homeAccentText)}>
                {peakDropSubtitle}
              </p>
            ) : null}
          </div>
          <div className="space-y-3 px-4 pb-4">
            <div className="relative" ref={dropdownRef}>
              <p className={cn("mb-2", gluceraHomeEyebrow)}>Compare activity</p>
              <div className="grid grid-cols-3 gap-1 rounded-xl border-2 border-border bg-card p-1 dark:border-border/80">
                {QUICK_EXERCISES.map((ex) => (
                  <button
                    key={ex.id}
                    type="button"
                    onClick={() => {
                      setSelectedExercise(ex);
                      setShowMoreDropdown(false);
                    }}
                    className={cn(
                      "min-h-[36px] rounded-lg px-2 py-2 text-compact-sm font-semibold transition-all duration-200",
                      selectedExercise.id === ex.id && !showMoreDropdown
                        ? gluceraHomeInterventionChipSelected
                        : gluceraHomeInterventionChipIdle
                    )}
                  >
                    {ex.label}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setShowMoreDropdown((v) => !v)}
                  className={cn(
                    "flex min-h-[36px] items-center justify-center gap-0.5 rounded-lg px-2 py-2 text-compact-sm font-semibold transition-all duration-200",
                    showMoreDropdown || !QUICK_EXERCISES.find((e) => e.id === selectedExercise.id)
                      ? gluceraHomeInterventionChipSelected
                      : gluceraHomeInterventionChipIdle
                  )}
                >
                  <span className="truncate">
                    {QUICK_EXERCISES.find((e) => e.id === selectedExercise.id) ? "More" : selectedExercise.label}
                  </span>
                  <ChevronDown
                    className={`h-3 w-3 shrink-0 opacity-60 transition-transform ${showMoreDropdown ? "rotate-180" : ""}`}
                  />
                </button>
              </div>
              {showMoreDropdown && (
                <div className="absolute left-0 right-0 top-[calc(100%+6px)] z-50 max-h-44 overflow-y-auto rounded-lg border-2 border-border bg-popover shadow-lg">
                  {ALL_ACTIVITIES.map((act) => (
                    <button
                      key={act.id}
                      type="button"
                      onClick={() => {
                        setSelectedExercise(act);
                        setShowMoreDropdown(false);
                      }}
                      className={cn(
                        "flex min-h-9 w-full items-center border-b border-border/30 px-3 text-left text-sm transition-colors last:border-0",
                        selectedExercise.id === act.id
                          ? gluceraHomeInterventionDropdownSelected
                          : "font-normal text-foreground hover:bg-muted/50"
                      )}
                    >
                      {act.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {logButtonLabel ? (
            <button
              type="button"
              onClick={() =>
                onStartIntervention?.({
                  activityKey: selectedExercise.activityKey,
                  label: selectedExercise.label,
                })
              }
              className={cn(
                "flex min-h-11 w-full items-center justify-center px-4 py-2.5",
                gluceraHomeInterventionStartButton
              )}
            >
              <span className="text-center text-compact-sm font-bold leading-snug">
                {logButtonLabel}
              </span>
            </button>
            ) : null}
          </div>
        </div>
        ) : null}

        {showGlucoseSummary ? (
        <div className="rounded-xl border border-border/60 bg-muted/20 p-4">
          <button
            type="button"
            onClick={() => setDetailOpen((v) => !v)}
            aria-expanded={detailOpen}
            className="flex w-full flex-col gap-3 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                {nowGl != null ? (
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-black tabular-nums text-foreground">
                    {Math.round(nowGl)}
                  </span>
                  <span className="text-2xs font-semibold uppercase tracking-wide text-muted-foreground">
                    now
                  </span>
                </div>
                ) : null}
                {nowGl != null && noActionPeakGl != null ? (
                <span className="text-muted-foreground/70">→</span>
                ) : null}
                {noActionPeakGl != null ? (
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-black tabular-nums text-red-500">
                    {Math.round(noActionPeakGl)}
                  </span>
                  <span className="text-2xs font-semibold uppercase tracking-wide text-muted-foreground">
                    peak
                  </span>
                </div>
                ) : null}
              </div>
              <span className={cn("flex shrink-0 items-center gap-0.5 text-xs font-semibold", homeAccentLink)}>
                {detailOpen ? "See less" : "See more"}
                <ChevronDown
                  className={cn("h-3.5 w-3.5 transition-transform", detailOpen && "rotate-180")}
                />
              </span>
            </div>
            {dropRounded != null && dropRounded > 0 ? (
              <span
                className={cn(
                  "inline-flex w-fit max-w-full items-center rounded-full px-2.5 py-1",
                  homeAccentChip,
                  gluceraHomeChipBadge
                )}
              >
                {selectedExercise.label} −{dropRounded} mg/dL
              </span>
            ) : null}
          </button>

          {detailOpen && (
          <div className="mt-4">
          <div className="grid grid-cols-2 gap-4">
            {nowGl != null ? (
            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">
                Now
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-black text-foreground">{Math.round(nowGl)}</span>
                <span className="text-sm text-muted-foreground">MG/DL • BASELINE</span>
              </div>
            </div>
            ) : null}

            {noActionPeakGl != null ? (
            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">
                Predicted Peak
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-black text-red-500">{Math.round(noActionPeakGl)}</span>
                {peakMin != null ? (
                <div className="flex flex-col">
                  <span className="text-xs font-bold uppercase text-red-500">in {peakMin} min</span>
                  <span className="text-xs text-muted-foreground">No action</span>
                </div>
                ) : null}
              </div>
            </div>
            ) : null}
          </div>

          {bestWindow != null ? (
          <div className="mt-3 flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2 text-xs">
            <div className="flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="font-semibold text-foreground">WINDOW {formatTime(bestWindow)}</span>
            </div>
            <span className={cn("font-medium", homeAccentText)}>• ACTION OPEN</span>
          </div>
          ) : null}

          {/* Graph */}
          {hasRenderableChart && (
            <div className="mt-4 h-40">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="glucoseGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ef4444" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="interventionGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6b7280" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#6b7280" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-border/30" />
                  <XAxis 
                    dataKey="t" 
                    stroke="currentColor" 
                    className="text-muted-foreground text-compact-xs"
                    tick={CHART_AXIS_TICK}
                    tickFormatter={(v) => `${v}m`}
                  />
                  <YAxis 
                    stroke="currentColor" 
                    className="text-muted-foreground text-compact-xs"
                    tick={CHART_AXIS_TICK}
                    domain={["auto", "auto"]}
                    width={32}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="noAction"
                    stroke="#ef4444"
                    strokeWidth={2.5}
                    fill="url(#glucoseGradient)"
                    name="No Action"
                    connectNulls
                  />
                  {interventionCurvePoints.length > 0 ? (
                  <Area
                    type="monotone"
                    dataKey="withAction"
                    stroke="#6b7280"
                    strokeWidth={2.5}
                    fill="url(#interventionGradient)"
                    name={`With ${selectedExercise.label}`}
                    connectNulls
                  />
                  ) : null}
                  {nowGl != null ? (
                    <>
                      <ReferenceLine y={nowGl} stroke="#666" strokeDasharray="3 3" />
                      <ReferenceDot
                        x={0}
                        y={nowGl}
                        r={4}
                        fill="#000"
                        stroke="#fff"
                        strokeWidth={2}
                      />
                    </>
                  ) : null}
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Legend */}
          {hasRenderableChart && (
            <div className="mt-3 flex items-center justify-center gap-4 text-xs">
              <div className="flex items-center gap-1.5">
                <div className="h-0.5 w-6 bg-red-500"></div>
                <span className="font-medium text-muted-foreground">No Action</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="h-0.5 w-6 bg-gray-500"></div>
                <span className="font-medium text-muted-foreground">With {selectedExercise.label}</span>
              </div>
            </div>
          )}

          {/* Comparison */}
          {(noActionPeakGl != null || newPeakGl != null) ? (
          <div className="mt-4 grid grid-cols-2 gap-3">
            {noActionPeakGl != null ? (
            <div className="rounded-lg bg-red-50 p-3 text-center dark:bg-red-950/20">
              <div className="text-xs font-semibold uppercase tracking-wide text-red-600 dark:text-red-400 mb-1">
                No Action
              </div>
              <div className="text-3xl font-black text-red-500">{Math.round(noActionPeakGl)}</div>
            </div>
            ) : null}
            {newPeakGl != null ? (
            <div className="rounded-lg bg-[#737DFE]/8 p-3 text-center dark:bg-[#737DFE]/12 relative">
              <div className="absolute -left-3 top-1/2 -translate-y-1/2 flex h-8 w-8 items-center justify-center rounded-full bg-[#737DFE] text-white shadow-lg">
                →
              </div>
              <div className={cn("text-xs font-semibold uppercase tracking-wide mb-1", homeAccentText)}>
                {selectedExercise.label}
              </div>
              <div className="flex items-baseline justify-center gap-1">
                <div className={cn("text-3xl font-black", homeAccentText)}>{Math.round(newPeakGl)}</div>
                {dropRounded != null && dropRounded > 0 ? (
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#737DFE] text-xs font-bold text-white">
                    −{dropRounded}
                  </div>
                ) : null}
              </div>
            </div>
            ) : null}
          </div>
          ) : null}
          </div>
          )}
        </div>
        ) : null}
      </div>
    </Card>
    ) : null}

    <HomeBbsPillarsSummary
      homeData={homeData}
      selectedDate={selectedDate}
      showFourPillars={showScorePillars}
    />

    {homeData?.["Health Insights"] ? (
      <HealthInsightsHomeCard healthInsights={homeData["Health Insights"]} />
    ) : null}

    <PoweringPredictionCard items={homeData?.["Powering This Prediction"]} />

  </div>
  );
};

export default GlucoseInterventionCard;